import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  Calendar, 
  Users, 
  BarChart3, 
  Building2, 
  Shield, 
  Clock,
  CheckCircle2,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  FileText,
  Package,
  Bell,
  Wallet,
  UserCog,
  MessageCircle,
  HardDrive,
  FileCheck,
  Sparkles,
  PenLine,
  X,
  Phone,
  Mail,
  User,
  Building,
  Download,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Custom requirement form state
  const [showRequirementForm, setShowRequirementForm] = useState(false);
  const [requirementLoading, setRequirementLoading] = useState(false);
  const requirementModalRef = useRef(null);
  const [requirementForm, setRequirementForm] = useState({
    name: '',
    phone: '',
    email: '',
    venueName: '',
    requirement: ''
  });

  // Scroll modal to top when it opens
  useEffect(() => {
    if (showRequirementForm && requirementModalRef.current) {
      // Force scroll to top immediately
      requirementModalRef.current.scrollTop = 0;
      // Also use scrollTo for better browser support
      setTimeout(() => {
        if (requirementModalRef.current) {
          requirementModalRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
      }, 0);
    }
  }, [showRequirementForm]);

  // Handle custom requirement form submission
  const handleRequirementSubmit = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    if (!requirementForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    if (!requirementForm.phone.trim() || requirementForm.phone.trim().length < 10) {
      toast({ title: 'Valid phone number is required', variant: 'destructive' });
      return;
    }
    
    setRequirementLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/visitor/custom-requirement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requirementForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Request Submitted!',
          description: 'Our team will contact you soon.',
        });
        setShowRequirementForm(false);
        setRequirementForm({ name: '', phone: '', email: '', venueName: '', requirement: '' });
      } else {
        // Handle validation errors (FastAPI returns array of errors)
        let errorMessage = 'Please try again';
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(e => e.msg || e.message || 'Invalid input').join(', ');
          }
        }
        toast({
          title: 'Submission Failed',
          description: errorMessage,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Unable to submit. Please try again later.',
        variant: 'destructive'
      });
    } finally {
      setRequirementLoading(false);
    }
  };

  // Actual app features
  const features = [
    {
      icon: Calendar,
      title: 'Booking Management',
      description: 'Manage venue bookings with calendar view, multi-day events, time slots, and booking status tracking.'
    },
    {
      icon: Users,
      title: 'Lead Management',
      description: 'Track inquiries, set follow-up dates, convert leads to bookings, and never miss a potential client.'
    },
    {
      icon: Building2,
      title: 'Multi-Venue Support',
      description: 'Manage up to 4 venues from a single dashboard with separate capacity and pricing for each.'
    },
    {
      icon: UserCog,
      title: 'Staff & Role Management',
      description: 'Add staff members with customizable permissions - control who can view, create, edit or delete data.'
    },
    {
      icon: Wallet,
      title: 'Accounts & Expenses',
      description: 'Track income, expenses, payment modes, and generate financial reports with GST calculations.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reports',
      description: 'Booking analytics, financial reports, revenue trends, and monthly performance tracking.'
    },
    {
      icon: Package,
      title: 'Inventory Management',
      description: 'Track venue inventory items, quantities, and maintain stock levels for events.'
    },
    {
      icon: Bell,
      title: 'Notifications & Reminders',
      description: 'Push notifications for new bookings, payment reminders, and follow-up alerts.'
    },
    {
      icon: Shield,
      title: 'Data Security & Safety',
      description: 'Backup all your data securely and restore anytime. Role-based access control and activity logs.'
    }
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: 'Error',
        description: 'Please enter both email and password',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      console.log('LandingPage login with rememberMe:', rememberMe);
      const result = await login(email, password, rememberMe);
      
      if (result.requiresOTP) {
        navigate('/login', { state: { email, requiresOTP: true, rememberMe } });
        return;
      }
      
      if (result.success) {
        toast({
          title: 'Welcome back!',
          description: 'Login successful'
        });
        navigate('/dashboard');
      } else {
        toast({
          title: 'Login Failed',
          description: result.error || 'Invalid credentials',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Something went wrong',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/25">
                <span className="text-white font-bold text-xs tracking-tight">MVM</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">MyVenueMate</h1>
                <p className="text-[10px] text-slate-400 -mt-1">Venue Management Simplified</p>
              </div>
            </div>
            
            {/* Mobile Android Download Button - Flashing */}
            <a 
              href="/downloads/MyVenueMate.apk"
              download="MyVenueMate.apk"
              className="md:hidden flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-semibold rounded-full shadow-lg shadow-green-500/30 animate-pulse hover:shadow-green-500/50 transition-all"
            >
              <Smartphone className="w-4 h-4" />
              <span>Get Android App</span>
              <Download className="w-3 h-3" />
            </a>
            
            {/* Desktop nav links */}
            <nav className="hidden md:flex items-center gap-6">
              <a 
                href="/downloads/MyVenueMate.apk"
                download="MyVenueMate.apk"
                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-semibold rounded-full shadow-lg shadow-green-500/30 animate-pulse hover:shadow-green-500/50 transition-all"
              >
                <Smartphone className="w-4 h-4" />
                <span>Android App</span>
                <Download className="w-3 h-3" />
              </a>
              <a href="#features" className="text-sm text-slate-300 hover:text-white transition-colors">Features</a>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/register')}
                className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
              >
                Start Free Trial
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <section className="min-h-[calc(100vh-4rem)] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 w-full">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              
              {/* Left Side - About App */}
              <div className="order-2 lg:order-1">
                {/* Headline */}
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                  Manage Your
                  <span className="block bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    Venue Business
                  </span>
                  Effortlessly
                </h2>
                
                {/* Description */}
                <p className="text-lg text-slate-400 mb-8 max-w-lg">
                  Complete venue management solution for banquet halls, marriage venues, and event spaces. 
                  Manage bookings, leads, staff, inventory, expenses, and analytics - all in one place.
                </p>
                
                {/* Feature highlights */}
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    'Booking Calendar',
                    'Lead Tracking',
                    'Staff Management',
                    'Multi-Venue Support',
                    'Financial Reports',
                    'Inventory Tracking'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Hero Image */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-slate-700/50">
                  <img 
                    src="https://images.pexels.com/photos/32854451/pexels-photo-32854451.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                    alt="Open Lawn Venue with Event Setup"
                    className="w-full h-56 sm:h-72 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-medium">Built for Indian Venue Owners</p>
                    <p className="text-sm text-slate-300">Local support • Made in India</p>
                  </div>
                </div>
              </div>

              {/* Right Side - Login Form */}
              <div className="order-1 lg:order-2">
                <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8 shadow-2xl shadow-black/25">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Welcome Back</h3>
                    <p className="text-slate-400">Sign in to manage your venues</p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-300">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20 h-12"
                        disabled={loading}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-slate-300">Password</Label>
                        <button 
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-teal-500 focus:ring-teal-500/20 h-12 pr-10"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer select-none py-2">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => {
                            setRememberMe(e.target.checked);
                            console.log('Remember me changed:', e.target.checked);
                          }}
                          className="w-5 h-5 rounded border-slate-600 bg-slate-900/50 text-teal-500 focus:ring-teal-500/20 cursor-pointer"
                        />
                        <span className="text-sm text-slate-400">Keep me signed in</span>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold shadow-lg shadow-teal-500/25 transition-all duration-300"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 bg-slate-800/50 text-slate-500">New to MyVenueMate?</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button
                      type="button"
                      className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold transition-all duration-300"
                      onClick={() => navigate('/register')}
                    >
                      Register - Start 15-Day Free Trial
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg shadow-orange-500/30 transition-all duration-300 animate-pulse hover:animate-none"
                      onClick={() => {
                        // Auto-login with demo credentials
                        setEmail('demo@myvenuemate.in');
                        setPassword('Demo@2025');
                        toast({
                          title: 'Demo Mode',
                          description: 'Logging into demo account. Note: Changes will not be saved.',
                        });
                        setTimeout(() => {
                          document.querySelector('form')?.requestSubmit();
                        }, 500);
                      }}
                    >
                      ✨ Try Demo Account
                    </Button>
                  </div>

                  <p className="text-center text-xs text-slate-500 mt-4">
                    No credit card required • Demo account is read-only
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                All the Features You Need
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Everything to manage your venue business efficiently - from bookings to finances.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="group p-6 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-teal-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/5"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coming Soon Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-4">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-400">Coming Soon</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Exciting Features on the Way
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                We are constantly working to make MyVenueMate even better for you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {/* WhatsApp Integration */}
              <div className="group p-6 rounded-xl bg-gradient-to-b from-green-900/20 to-slate-800/50 border border-green-500/30 hover:border-green-500/50 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">WhatsApp Integration</h3>
                <p className="text-slate-400 text-sm">Send booking confirmations, reminders, and updates directly to customers via WhatsApp.</p>
                <div className="mt-4 inline-block px-3 py-1 rounded-full bg-green-500/10 text-green-400 text-xs font-medium">
                  Coming Soon
                </div>
              </div>

              {/* Google Drive Integration */}
              <div className="group p-6 rounded-xl bg-gradient-to-b from-blue-900/20 to-slate-800/50 border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <HardDrive className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Google Drive Integration</h3>
                <p className="text-slate-400 text-sm">Automatically backup your data and store event photos securely in Google Drive.</p>
                <div className="mt-4 inline-block px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium">
                  Coming Soon
                </div>
              </div>

              {/* Quotation Creation */}
              <div className="group p-6 rounded-xl bg-gradient-to-b from-orange-900/20 to-slate-800/50 border border-orange-500/30 hover:border-orange-500/50 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <FileCheck className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Quotation with Venue Profile</h3>
                <p className="text-slate-400 text-sm">Create professional quotations with your venue branding and share them instantly with clients.</p>
                <div className="mt-4 inline-block px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-medium">
                  Coming Soon
                </div>
              </div>

              {/* Custom Requirement - Clickable */}
              <div 
                onClick={() => setShowRequirementForm(true)}
                className="group p-6 rounded-xl bg-gradient-to-b from-pink-900/20 to-slate-800/50 border border-pink-500/30 hover:border-pink-500/50 transition-all duration-300 cursor-pointer hover:scale-105"
                data-testid="custom-requirement-card"
              >
                <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <PenLine className="w-6 h-6 text-pink-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Custom Requirement</h3>
                <p className="text-slate-400 text-sm">Have a specific feature in mind? Let us know and we&apos;ll build it for you!</p>
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/20 text-pink-400 text-xs font-medium">
                  <ArrowRight className="w-3 h-3" />
                  Submit Request
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Custom Requirement Modal - Rendered via Portal */}
        {showRequirementForm && ReactDOM.createPortal(
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
              onClick={() => setShowRequirementForm(false)}
            />
            {/* Modal - Fixed positioned independently */}
            <div 
              ref={requirementModalRef}
              className="fixed z-[10000] left-4 right-4 sm:left-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 top-4 sm:top-[10vh] bg-slate-900 border border-slate-700 rounded-2xl p-6 animate-in fade-in zoom-in duration-200 max-h-[90vh] overflow-y-auto"
            >
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRequirementForm(false);
                }}
                className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors z-10"
                data-testid="close-requirement-modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-xl bg-pink-500/20 flex items-center justify-center mx-auto mb-3">
                  <PenLine className="w-7 h-7 text-pink-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Custom Requirement</h2>
                <p className="text-slate-400 text-sm mt-1">Tell us what feature you need</p>
              </div>

              {/* Form */}
              <form onSubmit={handleRequirementSubmit} className="space-y-4">
                {/* Name - Mandatory */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={requirementForm.name}
                    onChange={(e) => setRequirementForm({ ...requirementForm, name: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="requirement-name-input"
                    required
                  />
                </div>

                {/* Phone - Mandatory */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={requirementForm.phone}
                    onChange={(e) => setRequirementForm({ ...requirementForm, phone: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="requirement-phone-input"
                    required
                  />
                </div>

                {/* Email - Optional */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email <span className="text-slate-500 text-xs">(optional)</span>
                  </Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={requirementForm.email}
                    onChange={(e) => setRequirementForm({ ...requirementForm, email: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="requirement-email-input"
                  />
                </div>

                {/* Venue Name - Optional */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Venue Name <span className="text-slate-500 text-xs">(optional)</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Your venue name"
                    value={requirementForm.venueName}
                    onChange={(e) => setRequirementForm({ ...requirementForm, venueName: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-700 text-white"
                    data-testid="requirement-venue-input"
                  />
                </div>

                {/* Requirement - Optional */}
                <div>
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <PenLine className="w-4 h-4" />
                    Your Requirement <span className="text-slate-500 text-xs">(optional)</span>
                  </Label>
                  <Textarea
                    placeholder="Describe the feature or customization you need..."
                    value={requirementForm.requirement}
                    onChange={(e) => setRequirementForm({ ...requirementForm, requirement: e.target.value })}
                    className="mt-1 bg-slate-800 border-slate-700 text-white min-h-[100px]"
                    data-testid="requirement-text-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={requirementLoading}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                  data-testid="submit-requirement-btn"
                >
                  {requirementLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center text-slate-500 text-xs mt-4">
                Our team will contact you within 24-48 hours
              </p>
            </div>
          </>,
          document.body
        )}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-teal-900/50 to-cyan-900/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Simplify Your Venue Management?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Start your 15-day free trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-slate-900 hover:bg-slate-100 font-semibold px-8"
                onClick={() => navigate('/register')}
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white bg-white/10 text-white font-semibold hover:bg-white/20 backdrop-blur-sm"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Features
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="text-white font-semibold">MyVenueMate</span>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-400">
                <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="/contact-us" className="hover:text-white transition-colors">Contact Us</a>
                <a href="mailto:info@myvenuemate.in" className="hover:text-white transition-colors">info@myvenuemate.in</a>
              </div>
              <p className="text-sm text-slate-500">
                © 2026 MYONK. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
